import type { MaybeRefOrGetter } from 'vue';
import { useI18n } from 'vue-i18n';

import userService from '@satellite/services/user-service';
import { getLocalStorageValue, setLocalStorageValue } from './useLocalStorage';
import {
  i18nLocaleToLanguagePreference,
  isAppSupportedI18nLocale,
  languagePreferenceToI18nLocale,
  LOCALE_STORAGE_KEY,
  useLocaleHelpers
} from './useLocaleHelpers';

export type UserLanguagePreference = {
  id?: string;
  languagePreference?: string | null;
};

/**
 * Syncs vue-i18n locale and user-scoped localStorage with the user's persisted `languagePreference`.
 * DB is the source of truth on login for app UI locales ({@link localeToReadableLanguageMap}).
 * When the DB tag is missing or not an app UI locale, user-scoped localStorage is applied and
 * PATCHed back to the API when it holds a supported locale. If user-scoped localStorage is empty,
 * the guest `language` key is migrated on login whenever the DB has no app-supported preference.
 *
 * Writes use {@link i18nLocaleToLanguagePreference} (BCP 47 normalization, same as Neutron).
 * Reads use {@link languagePreferenceToI18nLocale} (app UI locales only).
 */
export function useLanguagePreferenceSync(userId?: MaybeRefOrGetter<string | undefined>) {
  const { locale } = useI18n({ useScope: 'global' });
  const { setLocale } = useLocaleHelpers(userId);

  function getStoredAppLocale(userIdValue?: string): string | undefined {
    const storageKey = userIdValue ? `${LOCALE_STORAGE_KEY}_${userIdValue}` : LOCALE_STORAGE_KEY;
    if (typeof window === 'undefined' || window.localStorage.getItem(storageKey) == null) {
      return undefined;
    }

    const storedLocale = getLocalStorageValue<string>(
      LOCALE_STORAGE_KEY,
      undefined,
      userIdValue ? { userId: userIdValue } : undefined
    );
    if (storedLocale && isAppSupportedI18nLocale(storedLocale)) {
      return storedLocale.toLowerCase();
    }

    return undefined;
  }

  function getLoginFallbackLocale(
    user: UserLanguagePreference & { id: string }
  ): string | undefined {
    const storedUserLocale = getStoredAppLocale(user.id);
    if (storedUserLocale) {
      return storedUserLocale;
    }

    if (!languagePreferenceToI18nLocale(user.languagePreference)) {
      return getStoredAppLocale();
    }

    return undefined;
  }

  function persistI18nLocaleForUser(userIdValue: string | undefined, i18nLocale: string) {
    locale.value = i18nLocale;

    if (userIdValue) {
      setLocalStorageValue(LOCALE_STORAGE_KEY, i18nLocale, { userId: userIdValue });
      return;
    }

    setLocale(i18nLocale);
  }

  function applyLanguagePreferenceFromUser(user: UserLanguagePreference) {
    const i18nLocale = languagePreferenceToI18nLocale(user.languagePreference);
    if (!i18nLocale) {
      return;
    }

    persistI18nLocaleForUser(user.id, i18nLocale);
  }

  async function persistLanguagePreferenceToApi(userId: string, languagePreference: string) {
    try {
      await userService.updateUser(userId, { languagePreference });
    } catch (error) {
      console.warn('[useLanguagePreferenceSync] Failed to persist languagePreference:', error);
    }
  }

  async function persistLanguagePreferenceIfNeeded(user: UserLanguagePreference & { id: string }) {
    if (languagePreferenceToI18nLocale(user.languagePreference)) {
      return;
    }

    const storedUserLocale = getStoredAppLocale(user.id);
    const languagePreference = storedUserLocale
      ? i18nLocaleToLanguagePreference(storedUserLocale)
      : undefined;
    if (!languagePreference) {
      return;
    }

    await persistLanguagePreferenceToApi(user.id, languagePreference);
  }

  async function persistLanguagePreferenceOnChange(userId: string, newLocale: string) {
    const languagePreference = i18nLocaleToLanguagePreference(newLocale);
    if (!languagePreference) {
      return;
    }

    await persistLanguagePreferenceToApi(userId, languagePreference);
  }

  async function syncLanguagePreferenceOnLogin(user: UserLanguagePreference & { id: string }) {
    const dbLocale = languagePreferenceToI18nLocale(user.languagePreference);
    if (dbLocale) {
      applyLanguagePreferenceFromUser(user);
      return;
    }

    const localeToApply = getLoginFallbackLocale(user);
    if (!localeToApply) {
      return;
    }

    persistI18nLocaleForUser(user.id, localeToApply);
    await persistLanguagePreferenceIfNeeded(user);
  }

  return {
    applyLanguagePreferenceFromUser,
    persistLanguagePreferenceIfNeeded,
    persistLanguagePreferenceOnChange,
    syncLanguagePreferenceOnLogin
  };
}
